import dayjs from "dayjs";
import type { ChatBlock } from "../components/ChatBar/blocks/chat-block.types";
import { safeParseBlock } from "../components/ChatBar/blocks/safeParseBlock";
import { useCompanyStore } from "../store/company.store";
import { api } from "../utils/axios";
import { config } from "../utils/config";
import { DATE_FORMAT } from "../utils/dates";
import {
	type EventSourceMessage,
	fetchEventSource,
} from "../utils/eventsource";
import { getIdToken } from "../utils/firebase";
import {
	CHAT_HANDOFF_HEADERS,
	CHAT_HANDOFF_REQUEST_CONFIG,
} from "./chatHandoffHeader";

/**
 * Thrown internally when the chat stream is rejected with a 401 at open time,
 * so `sendMessageStream` can re-mint the Firebase idToken and retry once.
 */
class StreamAuthError extends Error {
	constructor() {
		super("Chat stream authentication failed");
		this.name = "StreamAuthError";
	}
}

/**
 * The server closed this chat session (409 on open, or an error frame saying
 * so). Sending into it again only fails again, so the UI must offer a new
 * chat instead of a retry.
 */
export class SessionEndedError extends Error {
	constructor() {
		super("This chat has ended.");
		this.name = "SessionEndedError";
	}
}

/**
 * equility-api puts this on the error frame when recon-ai refuses a session
 * that is bound to another company. The session cannot be continued, so the
 * turn is re-sent once in a fresh one. Mirrors CHAT_SESSION_COMPANY_MISMATCH in
 * equility-api `user/services/session-company-mismatch.ts`.
 */
export const SESSION_COMPANY_MISMATCH_CODE = "CHAT_SESSION_COMPANY_MISMATCH";

const SESSION_ENDED_TEXT =
	/chat has ended|session (?:is |has been )?closed|closed session/i;

// Types for user chat
export interface UserChatRequest {
	message: string;
	session_id?: string;
	session_name?: string;
}

export interface UserChatResponse {
	response: string;
	session_id: string;
	timestamp: string;
	turn_number?: number;
	request_id: string;
	generation_time_ms?: number;
	used_knowledge_base?: boolean;
	documents_found?: number;
	// Set when the message asked for a person (handoff created or refused).
	handoff?: unknown;
	handoff_error?: string | null;
}

export interface UserChatSuccessResponse {
	success: true;
	data: UserChatResponse;
}

export interface UserChatErrorResponse {
	success: false;
	error: {
		message: string;
		code?: string;
		details?: any;
	};
	request_id: string;
}

export interface ChatSession {
	id: string;
	name: string;
	created_at: string;
	updated_at: string;
	message_count: number;
}

// Streaming types
export interface StreamingEventData {
	content?: string;
	token_type?: string;
	model?: string;
	provider?: string;
	session_id?: string;
	message_id?: string;
	sequence?: number;
	finished?: boolean;
	error?: string;
	// Machine-readable reason on an `error` frame, when the API has one.
	code?: string;
	// Turn-level status. `human_agent` means a staff member has the chat, so
	// the AI did not answer and the turn ends with no text.
	status?: string;
	// Set on the `done` frame of a turn held back because a person has the chat.
	ai_paused?: boolean;
	context_used?: boolean;
	documents_found?: number;
	timing?: {
		total_time_ms?: number;
		retrieval_time_ms?: number;
		generation_time_ms?: number;
	};
}

export interface StreamingChatEvent {
	type: "start" | "token" | "error" | "complete" | "ping" | string;
	data: StreamingEventData;
	timestamp: string;
	sequence: number;
	message_id?: string;
	session_id?: string;
}

export interface StreamingCallbacks {
	onStart?: (event: StreamingChatEvent) => void;
	onToken?: (token: string, event: StreamingChatEvent) => void;
	onError?: (error: string, event?: StreamingChatEvent) => void;
	onComplete?: (event: StreamingChatEvent) => void;
	// A typed content block (citation/table/chart/…) arriving alongside the text
	// stream via the `structured_data` SSE event.
	onBlock?: (block: ChatBlock, event: StreamingChatEvent) => void;
	// A `status` frame (`human_agent`, `tool_call`, ...). Advisory; the turn
	// still ends with `complete`.
	onStatus?: (status: string, event: StreamingChatEvent) => void;
	// The API's typed result of a person request made in chat text: the
	// handoff it opened (or already had), and/or why it could not (e.g. 429).
	onHandoff?: (handoff: unknown, error: string | null) => void;
	// The session is closed server-side; start a new chat rather than retry.
	onSessionEnded?: () => void;
	// The session is bound to another company. Return the id of a fresh
	// session and the turn is re-sent in it, once; return nothing and the
	// refusal goes to onError.
	onSessionCompanyMismatch?: () => string | undefined;
}

/** An error frame carrying SESSION_COMPANY_MISMATCH_CODE, held for the re-send. */
interface SessionRefusal {
	text: string;
	event: StreamingChatEvent;
}

class UserChatApiService {
	private readonly basePath = "/user/chat";

	/**
	 * Get the current company ID from the store
	 */
	private getCompanyId(): number | null {
		return useCompanyStore.getState().id;
	}

	/**
	 * Send a message to the chat endpoint
	 */
	async sendMessage(request: UserChatRequest): Promise<UserChatResponse> {
		try {
			const companyId = this.getCompanyId();
			const url = companyId
				? `${this.basePath}?company_id=${companyId}`
				: this.basePath;

			console.log(
				"Sending chat message",
				companyId ? `with company_id: ${companyId}` : "without company",
			);

			const response = await api.post<
				UserChatSuccessResponse | UserChatErrorResponse
			>(url, request, CHAT_HANDOFF_REQUEST_CONFIG);

			if ("success" in response.data && response.data.success === false) {
				throw new Error(response.data.error.message);
			}

			return (response.data as UserChatSuccessResponse).data;
		} catch (error: any) {
			console.error("Chat API error:", error);
			if (error.response?.status === 409) throw new SessionEndedError();
			throw new Error(
				error.response?.data?.error?.message ||
					error.message ||
					"Failed to send message",
			);
		}
	}

	/**
	 * Get user's chat sessions
	 */
	async getSessions(): Promise<ChatSession[]> {
		try {
			const companyId = this.getCompanyId();
			const url = companyId
				? `${this.basePath}/sessions?company_id=${companyId}`
				: `${this.basePath}/sessions`;

			const response = await api.get<{ success: boolean; data: ChatSession[] }>(
				url,
			);
			return response.data.data || [];
		} catch (error: any) {
			console.error("Failed to get sessions:", error);
			return [];
		}
	}

	/**
	 * Create a new chat session
	 */
	async createSession(name?: string): Promise<ChatSession> {
		try {
			const companyId = this.getCompanyId();
			const url = companyId
				? `${this.basePath}/sessions?company_id=${companyId}`
				: `${this.basePath}/sessions`;

			const response = await api.post<{ success: boolean; data: ChatSession }>(
				url,
				{
					name: name || `Chat ${dayjs().format(DATE_FORMAT)}`,
				},
			);
			return response.data.data;
		} catch (error: any) {
			console.error("Failed to create session:", error);
			throw new Error(
				error.response?.data?.error?.message || "Failed to create session",
			);
		}
	}

	/**
	 * Delete a chat session
	 */
	async deleteSession(sessionId: string): Promise<void> {
		try {
			const companyId = this.getCompanyId();
			const url = companyId
				? `${this.basePath}/sessions/${sessionId}?company_id=${companyId}`
				: `${this.basePath}/sessions/${sessionId}`;

			await api.delete(url);
		} catch (error: any) {
			console.error("Failed to delete session:", error);
			throw new Error(
				error.response?.data?.error?.message || "Failed to delete session",
			);
		}
	}

	/**
	 * Close a chat session (marks as complete, idempotent)
	 */
	async closeSession(
		sessionId: string,
	): Promise<{ message: string; closed: boolean }> {
		try {
			const companyId = this.getCompanyId();
			const url = companyId
				? `${this.basePath}/sessions/${sessionId}/close?company_id=${companyId}`
				: `${this.basePath}/sessions/${sessionId}/close`;

			const response = await api.post<{ message: string; closed: boolean }>(
				url,
			);
			return response.data;
		} catch (error: any) {
			console.error("Failed to close session:", error);
			// Don't throw for close operations - they should be best-effort
			return { message: "Session close attempted", closed: false };
		}
	}

	/**
	 * Fire-and-forget close during page unload.
	 *
	 * `beforeunload` kills in-flight XHR, so this cannot go through the axios
	 * instance -- it uses `navigator.sendBeacon`, which the browser delivers
	 * after the document is gone. sendBeacon takes no relative-URL base and no
	 * headers, so the absolute URL is built here from `config.api.baseURL` the
	 * same way `streamMessage` does. A relative path would post to the page
	 * origin (the dev server on :3001) and 404.
	 *
	 * Unauthenticated by necessity: sendBeacon cannot carry an Authorization
	 * header. The endpoint is idempotent and only marks a session complete.
	 */
	closeSessionOnUnload(sessionId: string): boolean {
		const companyId = this.getCompanyId();
		const queryParams = companyId ? `?company_id=${companyId}` : "";
		// baseURL already ends with "/", so drop basePath's leading one.
		const pathWithoutLeadingSlash = this.basePath.replace(/^\//, "");
		const url = `${config.api.baseURL}${pathWithoutLeadingSlash}/sessions/${sessionId}/close${queryParams}`;
		return navigator.sendBeacon(url);
	}

	/**
	 * Check if the chat service is healthy
	 */
	async checkHealth(): Promise<boolean> {
		try {
			const response = await api.get<{ status: string; message: string }>(
				`${this.basePath}/health`,
			);
			return response.data.status === "healthy";
		} catch (error) {
			console.error("Chat health check failed:", error);
			return false;
		}
	}

	/**
	 * Send a message with streaming response using Server-Sent Events (SSE)
	 * Tokens are received progressively as they are generated
	 */
	async sendMessageStream(
		request: UserChatRequest,
		callbacks: StreamingCallbacks,
		signal?: AbortSignal,
		// Route-resolved company id. When the user is inside a company context the
		// caller passes the id derived from the current route, which is the
		// authoritative source (the persisted store can be stale on a deep link).
		// Forwarded as `company_id` so the backend mints the data-tool service
		// token; when absent the global chat answers general/RAG questions only.
		companyIdOverride?: number | null,
	): Promise<void> {
		const companyId = companyIdOverride ?? this.getCompanyId();
		const queryParams = companyId ? `?company_id=${companyId}` : "";
		// Remove leading slash from basePath since baseURL already ends with /
		const pathWithoutLeadingSlash = this.basePath.replace(/^\//, "");
		const url = `${config.api.baseURL}${pathWithoutLeadingSlash}/stream${queryParams}`;

		console.log("[UserChatApi] Starting stream to:", url);

		// Replaced by a copy with a fresh session id for the one re-send after
		// a company-mismatch refusal.
		let activeRequest = request;
		let refusal: SessionRefusal | null = null;

		// Firebase idTokens live ~1h. Attach a fresh token per turn (getIdToken
		// returns a valid, auto-refreshed token); if the server still rejects it
		// mid-session with a 401 at stream open, re-mint once with a forced
		// refresh and retry — mirroring the axios interceptor's 401 handling,
		// which fetchEventSource bypasses because it uses raw fetch.
		const openStream = (token: string | undefined): Promise<void> =>
			fetchEventSource(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
					...CHAT_HANDOFF_HEADERS,
					Accept: "text/event-stream",
				},
				body: JSON.stringify(activeRequest),
				signal,
				openWhenHidden: true, // Keep streaming even when tab is hidden
				onopen: async (response) => {
					if (response.status === 401) {
						// Expired/rejected idToken — throw so this attempt aborts and
						// onerror rethrows to reject, letting the caller re-mint + retry.
						throw new StreamAuthError();
					}
					if (response.status === 409) throw new SessionEndedError();
					if (!response.ok) {
						const errorText = await response.text();
						throw new Error(`Stream failed: ${response.status} ${errorText}`);
					}
					console.log("[UserChatApi] Stream connection opened");
				},
				onmessage: (ev: EventSourceMessage) => {
					if (!ev.data) return;

					try {
						const event: StreamingChatEvent = JSON.parse(ev.data);

						if (event.type === "handoff") {
							const data = (event.data ?? {}) as {
								handoff?: unknown;
								error?: unknown;
							};
							callbacks.onHandoff?.(
								data.handoff ?? null,
								typeof data.error === "string" && data.error
									? data.error
									: null,
							);
							return;
						}

						// A status can ride its own `status` frame or a token frame
						// with no text (how the session agent's tool statuses
						// arrive), so it is read off the data rather than the type.
						// recon-ai's paused turn sends `{status: "human_agent"}` then a
						// `done` frame with `ai_paused: true` and no text; either one
						// means a person has the chat.
						const status =
							event.data?.status ??
							(event.data?.ai_paused === true ? "human_agent" : undefined);
						if (typeof status === "string" && status) {
							callbacks.onStatus?.(status, event);
						}
						// Only the human_agent frame is consumed here. Every other
						// status frame falls through to the switch exactly as before.
						if (status === "human_agent") return;

						switch (event.type) {
							case "start":
								console.log(
									"[UserChatApi] Stream started:",
									event.data?.session_id,
								);
								callbacks.onStart?.(event);
								break;
							case "token":
								callbacks.onToken?.(event.data?.content || "", event);
								break;
							case "error": {
								// recon-ai puts the human-readable error in `content`
								// and only a boolean flag in `error` (despite the type).
								// Reading `error` here surfaced a useless "true" toast;
								// prefer the message in `content`.
								const errorMessage =
									event.data?.content ||
									(typeof event.data?.error === "string" && event.data.error
										? event.data.error
										: "The assistant hit an error. Please try again.");
								console.error(
									"[UserChatApi] Stream error event:",
									errorMessage,
								);
								if (event.data?.code === SESSION_COMPANY_MISMATCH_CODE) {
									// Held until the stream closes; see the re-send below.
									refusal = { text: errorMessage, event };
									break;
								}
								if (SESSION_ENDED_TEXT.test(errorMessage)) {
									callbacks.onSessionEnded?.();
									break;
								}
								callbacks.onError?.(errorMessage, event);
								break;
							}
							case "complete":
								console.log("[UserChatApi] Stream completed");
								callbacks.onComplete?.(event);
								break;
							case "ping":
								// Ignore ping events - just keep-alive
								break;
							case "structured_data": {
								// A typed content block (citation/table/chart/…) that
								// renders alongside the streamed text (ChatBar/blocks).
								// The raw payload is validated + normalized here so a
								// drifted/oversized/non-finite block degrades to an
								// UnsupportedBlock instead of crashing a renderer.
								const rawBlock = (event.data as { structured_data?: unknown })
									?.structured_data;
								if (rawBlock !== undefined && rawBlock !== null) {
									callbacks.onBlock?.(safeParseBlock(rawBlock), event);
								}
								break;
							}
							default:
								// Handle unknown event types gracefully
								if (event.data?.content) {
									callbacks.onToken?.(event.data.content, event);
								}
						}
					} catch (parseError) {
						console.warn(
							"[UserChatApi] Failed to parse SSE data:",
							ev.data,
							parseError,
						);
						// A frame that isn't valid JSON is an unexpected server-side
						// error surfaced as raw text (e.g. a gateway/proxy timeout
						// like "timeout of 60000ms exceeded"), not a chat event.
						// fetchEventSource delivers complete frames, so this is a
						// whole malformed frame, not a partial chunk. Previously it
						// was swallowed with only a console.warn, leaving the user
						// staring at an empty assistant bubble with no error and no
						// retry hint — surface it so the UI can render a failure.
						const rawMessage =
							typeof ev.data === "string" && ev.data.trim()
								? ev.data.trim()
								: "The assistant response could not be read.";
						callbacks.onError?.(rawMessage);
					}
				},
				onclose: () => {
					console.log("[UserChatApi] Stream connection closed");
				},
				onerror: (err) => {
					// ALWAYS throw. fetchEventSource stops retrying only when onerror
					// THROWS: the call site does `onerror?.(err) ?? retryInterval`, and
					// `??` reads a returned null as "no opinion" and substitutes the
					// default 1s interval. So the previous `return null` — commented
					// "Stop retrying on error" — did the exact opposite and re-POSTed
					// this entire turn (fresh LLM call + tool calls) once a second,
					// forever, while the unsettled promise left isStreaming stuck true
					// and the composer disabled.
					//
					// Throwing rejects the promise, so `runStream`'s catch re-raises to
					// useUserChat, which owns the user-facing copy. Deliberately no
					// `callbacks.onError` here: err.message is raw library text ("The
					// socket connection was closed unexpectedly...") and that catch
					// exists precisely to replace it with something human. An
					// AbortError propagates too and is filtered there by name.
					if (err instanceof StreamAuthError) throw err;
					if (err instanceof SessionEndedError) throw err;
					console.error("[UserChatApi] Stream error:", err);
					throw err;
				},
			});

		const runStream = async (
			forceRefresh: boolean,
			allowRetry: boolean,
		): Promise<void> => {
			const token = await getIdToken(forceRefresh);
			try {
				await openStream(token);
			} catch (err) {
				if (err instanceof SessionEndedError) {
					callbacks.onSessionEnded?.();
					return;
				}
				if (!(err instanceof StreamAuthError)) throw err;
				if (allowRetry) {
					// idToken expired mid-turn: re-mint once (forced) and retry.
					return runStream(true, false);
				}
				callbacks.onError?.("Your session expired. Please try again.");
			}
		};

		await runStream(false, true);
		if (!refusal) return;

		const freshSessionId = callbacks.onSessionCompanyMismatch?.();
		if (freshSessionId) {
			refusal = null;
			activeRequest = { ...request, session_id: freshSessionId };
			await runStream(false, true);
		}
		// Still refused, or the caller could not start a fresh session.
		// (Cast: TS cannot see the assignment made inside the stream callback.)
		const unresolved = refusal as SessionRefusal | null;
		if (unresolved) callbacks.onError?.(unresolved.text, unresolved.event);
	}
}

export const userChatApi = new UserChatApiService();
export default UserChatApiService;
